// ===========================================
// Users API Functions
// ===========================================
// API calls for user management endpoints (teachers, parents, students, families)

import { apiClient } from '../services/api';

// ===========================================
// TYPES
// ===========================================

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  role: 'ADMIN' | 'TEACHER' | 'PARENT' | 'STUDENT';
  isActive: boolean;
}

export interface Instrument {
  id: string;
  name: string;
  sortOrder: number;
  isActive: boolean;
  color: string | null;
}

export interface TeacherInstrument {
  id: string;
  Instrument: Instrument;
  isPrimary: boolean;
}

export interface Teacher {
  id: string;
  userId: string;
  bio: string | null;
  isActive: boolean;
  color: string | null;
  canHostMeetAndGreet: boolean;
  workingWithChildrenId?: string | null;
  User: User;
  TeacherInstrument: TeacherInstrument[];
  /** The login holds a staff role (list only): its email and active state are the owner's to change. */
  hasStaffRole?: boolean;
  /** That staff role (list only): its name and colour are the teacher's chip; its id, Office access. */
  staffRole?: { id: string; name: string; color: string | null } | null;
}

/**
 * A teacher as a PARENT sees them (`GET /teachers/available-for-booking`).
 * Name and phone only — parents and teachers see each other's phone, never
 * email (#305; Andrew, 2026-10-02).
 */
export interface ParentFacingTeacher {
  id: string;
  User: { id: string; firstName: string; lastName: string; phone: string | null };
  TeacherInstrument: TeacherInstrument[];
}

/** One lesson a teacher is involved in via per-week replacement (one row). */
export interface ReplacementInvolvementLesson {
  lessonId: string;
  lessonName: string;
  weeks: number[];
  dates: string[]; // ISO occurrence dates
  otherTeacher: { id: string; firstName: string; lastName: string } | null;
}

export interface TeacherReplacementInvolvement {
  /** Lessons this teacher normally teaches but is replaced for specific weeks. */
  beingReplaced: ReplacementInvolvementLesson[];
  /** Lessons this teacher is covering as the per-week replacement. */
  covering: ReplacementInvolvementLesson[];
}

/** Current "Reset Teacher Tasks" watermark for the school. */
export interface TaskResetInfo {
  cutoffDate: string; // ISO — note-tasks on/before this date are written off
  reason: string | null;
  createdAt: string;
  resetBy: { id: string; firstName: string; lastName: string } | null;
}

export interface TeacherAvailabilitySlot {
  id: string;
  teacherId: string;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/** A booked commitment that would fall outside availability after a window change. */
export interface AffectedLesson {
  date: string; // YYYY-MM-DD
  startTime: string;
  endTime: string;
  label: string;
  source: string;
}

// Teacher time-off / away periods (TeacherUnavailability)
export type TeacherUnavailabilityStatus = 'PENDING' | 'APPROVED' | 'DECLINED' | 'CANCELLED';

export interface TeacherUnavailabilityPeriod {
  id: string;
  schoolId: string;
  teacherId: string;
  startDate: string; // ISO (UTC midnight — calendar day)
  endDate: string; // ISO (UTC midnight — calendar day, inclusive)
  status: TeacherUnavailabilityStatus;
  reason: string | null;
  requestedById: string | null;
  reviewedById: string | null;
  reviewedAt: string | null;
  reviewNote: string | null;
  createdAt: string;
  updatedAt: string;
  /** Present on the admin pending-inbox listing only. */
  Teacher?: { id: string; User: { firstName: string; lastName: string } };
}

export interface TimeOffRangeInput {
  startDate: string; // YYYY-MM-DD
  endDate: string; // YYYY-MM-DD
  reason?: string;
}

export interface Contact {
  firstName: string;
  lastName: string;
  relationship: string;
  phone: string;
  email?: string;
  isEmergency?: boolean;
}

export interface Parent {
  id: string;
  userId: string;
  familyId: string;
  familyName: string;
  contact1FirstName: string;
  contact1LastName: string;
  contact1Email: string;
  contact1Phone: string;
  contact1Relationship: string;
  contact2FirstName: string | null;
  contact2LastName: string | null;
  contact2Email: string | null;
  contact2Phone: string | null;
  contact2Relationship: string | null;
  emergencyFirstName: string;
  emergencyLastName: string;
  emergencyPhone: string;
  emergencyRelationship: string;
  isPrimary: boolean;
  User: User;
  Family?: Family;
}

export interface AgeGroup {
  id: string;
  name: string;
  minAge: number | null;
  maxAge: number | null;
  color: string | null;
}

export interface Student {
  id: string;
  familyId: string | null;
  ageGroupId: string | null;
  firstName: string;
  lastName: string;
  birthDate: string | null;
  email: string | null;
  phone: string | null;
  notes: string | null;
  avatar: string | null;
  isActive: boolean;
  status: 'NEW' | 'WAITLISTED' | 'ACTIVE' | 'INACTIVE';
  /**
   * ⚠️ TWO casings: `GET /students` sends Prisma's PascalCase `Family`; only the
   * student's own-profile endpoints map it to `family`. Reading `family` on the
   * admin list left every student showing "No family assigned" (MNM, 2026-09-25).
   * Never read these directly: go through `getStudentFamily()`.
   */
  family?: Family;
  Family?: Family;
  /**
   * ⚠️ TWO casings, because the backend genuinely sends BOTH: the student and
   * parent services map the relation to camelCase `ageGroup`, while the lessons
   * list and several raw includes send Prisma's PascalCase `AgeGroup`. A read of
   * either compiles no matter which endpoint the value came from — that lie let
   * `student.ageGroup?.name` sit `undefined` on the lessons payload for months
   * (every student rendered as Steve) until the 2026-09-08 crash. Never read
   * these directly: go through `getStudentAgeGroup()`.
   */
  ageGroup?: AgeGroup | null;
  AgeGroup?: AgeGroup | null;
}

/**
 * The ONE way to read a student's age group off any payload. Prefers the raw
 * PascalCase relation, falls back to the mapped camelCase one, returns null
 * when neither is present (a student with no `ageGroupId`). Works on any
 * student-shaped object — enrolment rows, roster pills, family students.
 */
export function getStudentAgeGroup<T>(
  student: { ageGroup?: T | null; AgeGroup?: T | null } | null | undefined
): T | null {
  return student?.AgeGroup ?? student?.ageGroup ?? null;
}

/**
 * The ONE way to read a student's family off any payload — same shape and
 * precedence as `getStudentAgeGroup`.
 */
export function getStudentFamily<T>(
  student: { family?: T | null; Family?: T | null } | null | undefined
): T | null {
  return student?.Family ?? student?.family ?? null;
}

export interface Family {
  id: string;
  name: string;
  contactDetailsLocked?: boolean;
  deletionStatus?: 'ACTIVE' | 'SOFT_DELETED' | 'HARD_DELETE_PENDING';
  consentAccepted?: boolean;
  consentAcceptedAt?: string | null;
  consentDeclinedAt?: string | null;
  mediaExposure?: 'PENDING' | 'NO' | 'BLUR' | 'FULL';
  mediaExposureSetAt?: string | null;
  parents?: Parent[];
  students?: Student[];
  _count?: { parents: number; students: number };
}

/**
 * Messageable CONTACTS of one family.
 *
 * Structurally compatible with `ClassMembers` (lessons.api.ts) so the same
 * `ClassMessageModal` renders both.
 *
 * ⚠️ Unlike the class version, `id` is NOT a User id — it is a contact-scoped key, because a
 * family is reachable whether or not anyone has activated an account. A `Parent` row holds
 * two contacts with their own names and emails, and `userId` is null for anyone without a
 * login. Those contacts still receive the message by email; filtering on a login would hide
 * exactly the families a school most needs to reach.
 */
export interface FamilyMembers {
  familyName: string;
  parents: {
    /** Contact-scoped id (`<parentId>:1|2`) — a Parent row holds TWO contacts. */
    id: string;
    /** The login, where one exists. Null means email-only delivery. */
    userId: string | null;
    firstName: string;
    lastName: string;
    email: string;
    phone: string | null;
    role: 'PARENT';
    studentNames: string[];
  }[];
  students: {
    id: string;
    userId: string | null;
    firstName: string;
    lastName: string;
    email: string;
    phone: string | null;
    role: 'STUDENT';
    studentName: string;
  }[];
}

export interface FamilyImpact {
  familyId: string;
  name: string;
  studentCount: number;
  parentCount: number;
  primaryParentCount: number;
  totalInvoiceCount: number;
  paymentPlanCount: number;
}

interface ApiResponse<T> {
  status: string;
  data: T;
}

// ===========================================
// TEACHERS
// ===========================================

/** What a Send login link press returns (#589). Never the token or the URL. */
export interface LoginLinkResult {
  sentTo: string;
  /** ISO timestamp the link stops working. */
  expiresAt: string;
}

export const teachersApi = {
  /** `teacherId` is a TEACHER id. */
  sendLoginLink: (teacherId: string) =>
    apiClient
      .post<ApiResponse<LoginLinkResult>>(`/teachers/${teacherId}/login-link`)
      .then((res) => res.data),

  getAll: () =>
    apiClient.get<ApiResponse<Teacher[]>>('/teachers').then((res) => res.data),

  getAvailableForBooking: () =>
    apiClient
      .get<ApiResponse<ParentFacingTeacher[]>>('/teachers/available-for-booking')
      .then((res) => res.data),

  getById: (id: string) =>
    apiClient.get<ApiResponse<Teacher>>(`/teachers/${id}`).then((res) => res.data),

  getReplacementInvolvement: (id: string) =>
    apiClient
      .get<ApiResponse<TeacherReplacementInvolvement>>(`/teachers/${id}/replacement-involvement`)
      .then((res) => res.data),

  create: (data: {
    email: string;
    firstName: string;
    lastName: string;
    phone?: string;
    password?: string;
    bio?: string;
    instrumentIds?: string[];
    color?: string;
  }) => apiClient.post<ApiResponse<Teacher>>('/teachers', data).then((res) => res.data),

  update: (id: string, data: Partial<{ email: string; firstName: string; lastName: string; phone: string; bio: string; isActive: boolean; color: string | null; canHostMeetAndGreet: boolean; workingWithChildrenId: string }>) =>
    apiClient.patch<ApiResponse<Teacher>>(`/teachers/${id}`, data).then((res) => res.data),

  delete: (id: string) =>
    apiClient.delete<ApiResponse<void>>(`/teachers/${id}`),

  // Instrument management
  assignInstrument: (teacherId: string, instrumentId: string, isPrimary?: boolean) =>
    apiClient.post<ApiResponse<void>>(`/teachers/${teacherId}/instruments`, {
      instrumentId,
      isPrimary,
    }),

  removeInstrument: (teacherId: string, instrumentId: string) =>
    apiClient.delete<ApiResponse<void>>(`/teachers/${teacherId}/instruments/${instrumentId}`),

  setPrimaryInstrument: (teacherId: string, instrumentId: string) =>
    apiClient.patch<ApiResponse<void>>(`/teachers/${teacherId}/instruments/${instrumentId}/primary`, {}),

  // Meet & Greet settings
  getMeetAndGreetHosts: () =>
    apiClient.get<ApiResponse<Teacher[]>>('/teachers/meet-and-greet/hosts').then((res) => res.data),

  toggleMeetAndGreet: (teacherId: string, canHost: boolean) =>
    apiClient.patch<ApiResponse<Teacher>>(`/teachers/${teacherId}/meet-and-greet`, { canHost }).then((res) => res.data),

  // Task summary
  getTaskSummary: () =>
    apiClient
      .get<ApiResponse<Record<string, { total: number; overdue: number }>>>('/teachers/task-summary')
      .then((res) => res.data),

  // Reset Teacher Tasks (watermark) — writes off outstanding incomplete-note
  // tasks up to a cutoff (default: now). Notes are not deleted.
  getTaskReset: () =>
    apiClient
      .get<ApiResponse<TaskResetInfo | null>>('/teachers/task-reset')
      .then((res) => res.data),

  resetTasks: (data?: { cutoffDate?: string; reason?: string }) =>
    apiClient
      .post<ApiResponse<TaskResetInfo>>('/teachers/reset-tasks', data ?? {})
      .then((res) => res.data),

  // Availability management
  getAvailability: (teacherId: string) =>
    apiClient
      .get<ApiResponse<TeacherAvailabilitySlot[]>>(`/teachers/${teacherId}/availability`)
      .then((res) => res.data),

  addAvailability: (teacherId: string, data: { dayOfWeek: number; startTime: string; endTime: string }) =>
    apiClient
      .post<ApiResponse<TeacherAvailabilitySlot[]>>(`/teachers/${teacherId}/availability`, data)
      .then((res) => res.data),

  updateAvailability: (
    teacherId: string,
    slotId: string,
    data: { dayOfWeek?: number; startTime?: string; endTime?: string; isActive?: boolean }
  ) =>
    apiClient
      .patch<ApiResponse<TeacherAvailabilitySlot[]>>(`/teachers/${teacherId}/availability/${slotId}`, data)
      .then((res) => res.data),

  // Preview which booked lessons would fall outside availability if a window is
  // changed to `proposed` (pass null to model deleting the window). Read-only.
  getAvailabilityAffectedLessons: (
    teacherId: string,
    slotId: string,
    proposed: { dayOfWeek: number; startTime: string; endTime: string } | null
  ) =>
    apiClient
      .post<ApiResponse<AffectedLesson[]>>(
        `/teachers/${teacherId}/availability/${slotId}/affected-lessons`,
        { proposed }
      )
      .then((res) => res.data),

  deleteAvailability: (teacherId: string, slotId: string) =>
    apiClient
      .delete<ApiResponse<TeacherAvailabilitySlot[]>>(`/teachers/${teacherId}/availability/${slotId}`)
      .then((res) => res.data),

  bulkSetAvailability: (teacherId: string, slots: Array<{ dayOfWeek: number; startTime: string; endTime: string }>) =>
    apiClient
      .put<ApiResponse<TeacherAvailabilitySlot[]>>(`/teachers/${teacherId}/availability`, { slots })
      .then((res) => res.data),

  // Time off / away periods (TeacherUnavailability).
  // Teacher self-service — teacherId is session-derived server-side.
  getMyUnavailability: () =>
    apiClient
      .get<ApiResponse<TeacherUnavailabilityPeriod[]>>('/teachers/me/unavailability')
      .then((res) => res.data),

  requestTimeOff: (data: TimeOffRangeInput) =>
    apiClient
      .post<ApiResponse<TeacherUnavailabilityPeriod>>('/teachers/me/unavailability', data)
      .then((res) => res.data),

  withdrawTimeOff: (periodId: string) =>
    apiClient
      .delete<ApiResponse<TeacherUnavailabilityPeriod>>(`/teachers/me/unavailability/${periodId}`)
      .then((res) => res.data),

  // Admin
  getUnavailability: (teacherId: string) =>
    apiClient
      .get<ApiResponse<TeacherUnavailabilityPeriod[]>>(`/teachers/${teacherId}/unavailability`)
      .then((res) => res.data),

  getPendingTimeOff: () =>
    apiClient
      .get<ApiResponse<TeacherUnavailabilityPeriod[]>>('/teachers/unavailability/pending')
      .then((res) => res.data),

  setTeacherAway: (teacherId: string, data: TimeOffRangeInput) =>
    apiClient
      .post<ApiResponse<TeacherUnavailabilityPeriod>>(`/teachers/${teacherId}/unavailability`, data)
      .then((res) => res.data),

  approveTimeOff: (teacherId: string, periodId: string, reviewNote?: string) =>
    apiClient
      .patch<ApiResponse<TeacherUnavailabilityPeriod>>(
        `/teachers/${teacherId}/unavailability/${periodId}/approve`,
        { reviewNote }
      )
      .then((res) => res.data),

  declineTimeOff: (teacherId: string, periodId: string, reviewNote?: string) =>
    apiClient
      .patch<ApiResponse<TeacherUnavailabilityPeriod>>(
        `/teachers/${teacherId}/unavailability/${periodId}/decline`,
        { reviewNote }
      )
      .then((res) => res.data),

  cancelTimeOff: (teacherId: string, periodId: string) =>
    apiClient
      .delete<ApiResponse<TeacherUnavailabilityPeriod>>(
        `/teachers/${teacherId}/unavailability/${periodId}`
      )
      .then((res) => res.data),

  // Existing commitments inside a prospective away range — the "you'll need to
  // handle these" preview. Read-only; flags, never changes.
  getAwayAffectedLessons: (teacherId: string, startDate: string, endDate: string) =>
    apiClient
      .post<ApiResponse<AffectedLesson[]>>(`/teachers/${teacherId}/unavailability/affected-lessons`, {
        startDate,
        endDate,
      })
      .then((res) => res.data),
};

// ===========================================
// PARENTS
// ===========================================

export interface CreateParentData {
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  password?: string;
  familyName?: string;
  contact1FirstName: string;
  contact1LastName: string;
  contact1Email: string;
  contact1Phone: string;
  contact1Relationship: string;
  contact2FirstName?: string;
  contact2LastName?: string;
  contact2Email?: string;
  contact2Phone?: string;
  contact2Relationship?: string;
  emergencyFirstName: string;
  emergencyLastName: string;
  emergencyPhone: string;
  emergencyRelationship: string;
}

export interface UpdateParentData {
  firstName?: string;
  lastName?: string;
  phone?: string;
  familyName?: string;
  isActive?: boolean;
  contact1FirstName?: string;
  contact1LastName?: string;
  contact1Email?: string;
  contact1Phone?: string;
  contact1Relationship?: string;
  contact2FirstName?: string | null;
  contact2LastName?: string | null;
  contact2Email?: string | null;
  contact2Phone?: string | null;
  contact2Relationship?: string | null;
  emergencyFirstName?: string;
  emergencyLastName?: string;
  emergencyPhone?: string;
  emergencyRelationship?: string;
  isPrimary?: boolean;
  contactDetailsLocked?: boolean;
}

// Family details with students and invoices
export interface StudentLesson {
  id: string;
  name: string;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  instrument: string | null;
  instrumentColor: string | null;
  lessonType: string | null;
  teacher: string | null;
  teacherColor: string | null;
  programLevel: string | null;
  programLevelColor: string | null;
}

export interface FamilyStudent {
  id: string;
  firstName: string;
  lastName: string;
  ageGroup: AgeGroup | null;
  notes: string | null;
  lessons: StudentLesson[];
}

export interface OutstandingInvoice {
  id: string;
  invoiceNumber: string;
  total: number;
  /** Paid so far and what is still owed (2026-09-27 — part-paid invoices are listed too). */
  amountPaid: number;
  balance: number;
  status: 'SENT' | 'PARTIALLY_PAID' | 'OVERDUE';
  dueDate: string;
}

export interface FamilyDetails extends Parent {
  /**
   * The family login's state (#589). `protected` = an ADMIN or staff-role login, whose
   * Send login link only the owner may press.
   */
  loginStatus?: { neverLoggedIn: boolean; protected: boolean };
  students: FamilyStudent[];
  outstandingInvoices: OutstandingInvoice[];
}

export const parentsApi = {
  /** `parentId` is a PARENT id (any contact row with a login). */
  sendLoginLink: (parentId: string) =>
    apiClient
      .post<ApiResponse<LoginLinkResult>>(`/parents/${parentId}/login-link`)
      .then((res) => res.data),

  getAll: () =>
    apiClient.get<ApiResponse<Parent[]>>('/parents').then((res) => res.data),

  getById: (id: string) =>
    apiClient.get<ApiResponse<Parent>>(`/parents/${id}`).then((res) => res.data),

  getDetails: (id: string) =>
    apiClient.get<ApiResponse<FamilyDetails>>(`/parents/${id}/details`).then((res) => res.data),

  create: (data: CreateParentData) =>
    apiClient.post<ApiResponse<Parent>>('/parents', data).then((res) => res.data),

  update: (id: string, data: UpdateParentData) =>
    apiClient.patch<ApiResponse<Parent>>(`/parents/${id}`, data).then((res) => res.data),

  delete: (id: string) =>
    apiClient.delete<ApiResponse<void>>(`/parents/${id}`),
};

// ===========================================
// STUDENTS
// ===========================================

export type EnrollmentTermFilter = 'current' | 'all';

export interface StudentEnrollment {
  id: string;
  lessonId: string;
  enrolledAt: string;
  isWaitlisted?: boolean;
  lesson: {
    id: string;
    name: string;
    dayOfWeek: number | null;
    startTime: string | null;
    endTime: string | null;
    instrument: string | null;
    lessonType: string | null;
    teacher: string | null;
    location: string | null;
    room: string | null;
    term: {
      id: string;
      name: string;
      startDate: string;
      endDate: string;
    } | null;
  };
}

export const studentsApi = {
  getAll: (): Promise<Student[]> =>
    apiClient.get<{ status: string; data: Student[] }>('/students').then((res) => res.data),

  getById: (id: string): Promise<Student> =>
    apiClient.get<{ status: string; data: Student }>(`/students/${id}`).then((res) => res.data),

  getEnrollments: (
    id: string,
    termFilter: EnrollmentTermFilter = 'current'
  ): Promise<StudentEnrollment[]> =>
    apiClient
      .get<{ status: string; data: StudentEnrollment[] }>(
        `/students/${id}/enrollments`,
        { params: { termFilter } }
      )
      .then((res) => res.data),

  create: (data: {
    firstName: string;
    lastName: string;
    birthDate?: string;
    familyId?: string;
    notes?: string;
  }): Promise<Student> => apiClient.post<{ status: string; data: Student }>('/students', data).then((res) => res.data),

  update: (
    id: string,
    data: Partial<{
      firstName: string;
      lastName: string;
      birthDate: string | null;
      familyId: string | null;
      email: string | null;
      phone: string | null;
      notes: string | null;
      isActive: boolean;
      status: 'NEW' | 'ACTIVE' | 'INACTIVE';
    }>
  ): Promise<Student> => apiClient.patch<{ status: string; data: Student }>(`/students/${id}`, data).then((res) => res.data),

  delete: (id: string): Promise<{ status: string }> =>
    apiClient.delete<{ status: string }>(`/students/${id}`),

  // Family management
  assignToFamily: (studentId: string, familyId: string): Promise<Student> =>
    apiClient.post<{ status: string; data: Student }>(`/students/${studentId}/family`, { familyId }).then((res) => res.data),

  removeFromFamily: (studentId: string): Promise<Student> =>
    apiClient.delete<{ status: string; data: Student }>(`/students/${studentId}/family`).then((res) => res.data),
};

// ===========================================
// PARENT PROFILE (Self-Service)
// ===========================================

export interface ParentContact {
  firstName: string;
  lastName: string;
  email?: string;
  phone: string;
  relationship: string;
}

export interface ParentProfile {
  id: string;
  userId: string;
  isPrimary: boolean;
  familyName: string;
  contactDetailsLocked: boolean;
  user: {
    email: string;
    firstName: string;
    lastName: string;
    phone: string | null;
  };
  contact1: ParentContact;
  contact2: ParentContact | null;
  emergencyContact: {
    firstName: string;
    lastName: string;
    phone: string;
    relationship: string;
  };
}

export interface UpdateParentProfileData {
  // User fields
  firstName?: string;
  lastName?: string;
  phone?: string | null;

  // NOTE: familyName is NOT included — parents cannot rename their own family.
  // Only admins can change a family name. The backend ignores familyName here.

  // Contact 1 (Primary)
  contact1FirstName?: string;
  contact1LastName?: string;
  contact1Email?: string;
  contact1Phone?: string;
  contact1Relationship?: string;

  // Contact 2
  contact2FirstName?: string | null;
  contact2LastName?: string | null;
  contact2Email?: string | null;
  contact2Phone?: string | null;
  contact2Relationship?: string | null;

  // Emergency Contact
  emergencyFirstName?: string;
  emergencyLastName?: string;
  emergencyPhone?: string;
  emergencyRelationship?: string;
}

export const parentProfileApi = {
  getProfile: () =>
    apiClient.get<ApiResponse<ParentProfile>>('/users/me/profile').then((res) => res.data),

  updateProfile: (data: UpdateParentProfileData) =>
    apiClient.patch<ApiResponse<ParentProfile>>('/users/me/profile', data).then((res) => res.data),
};

// Type helper for parent profile hooks - the API returns { status, data } and we extract just data
export type ParentProfileResponse = ParentProfile;

// ===========================================
// FAMILIES
// ===========================================

// Families types live alongside the families API surface, but the
// MediaExposure union itself is owned by consent.api.ts.
import type { MediaExposure } from './consent.api';
export type FamilyMediaExposure = MediaExposure;

export interface ParentSummary {
  id: string;
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  relationship: string;
  isPrimary: boolean;
  isActive: boolean;
  /** `lastLoginAt` is null: they have never logged in (#589). */
  neverLoggedIn: boolean;
}

export interface FamilyListItem {
  id: string;
  name: string;
  mediaExposure: FamilyMediaExposure;
  contactDetailsLocked: boolean;
  primaryParent: ParentSummary | null;
  secondaryParents: ParentSummary[];
  studentCount: number;
}

export const familiesApi = {
  getAll: () =>
    apiClient.get<ApiResponse<Family[]>>('/families').then((res) => res.data),

  getAdminList: () =>
    apiClient
      .get<ApiResponse<FamilyListItem[]>>('/families', { params: { view: 'admin' } })
      .then((res) => res.data),

  getById: (id: string) =>
    apiClient.get<ApiResponse<Family>>(`/families/${id}`).then((res) => res.data),

  getImpact: (id: string) =>
    apiClient
      .get<ApiResponse<FamilyImpact>>(`/families/${id}/impact`)
      .then((res) => res.data),

  /**
   * Messageable members of one family — parents, plus students with a user account.
   *
   * Returns the same `{ parents, students }` shape as `/lessons/:id/class-members`, so the
   * shared `ClassMessageModal` renders either without knowing which it was handed.
   */
  getMembers: (id: string) =>
    apiClient
      .get<ApiResponse<FamilyMembers>>(`/families/${id}/members`)
      .then((res) => res.data),

  create: (data: { name: string }) =>
    apiClient.post<ApiResponse<Family>>('/families', data).then((res) => res.data),

  update: (id: string, data: Partial<{ name: string; isActive: boolean }>) =>
    apiClient.patch<ApiResponse<Family>>(`/families/${id}`, data).then((res) => res.data),

  delete: (id: string) =>
    apiClient.delete<ApiResponse<void>>(`/families/${id}`),

  // Member management
  addStudent: (familyId: string, studentId: string) =>
    apiClient.post<ApiResponse<void>>(`/families/${familyId}/students`, { studentId }),

  removeStudent: (familyId: string, studentId: string) =>
    apiClient.delete<ApiResponse<void>>(`/families/${familyId}/students/${studentId}`),

  addParent: (familyId: string, parentId: string) =>
    apiClient.post<ApiResponse<void>>(`/families/${familyId}/parents`, { parentId }),

  removeParent: (familyId: string, parentId: string) =>
    apiClient.delete<ApiResponse<void>>(`/families/${familyId}/parents/${parentId}`),
};
